import { useQuery } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { PerformanceFigures } from "../../lib/crm-types";
import { cn } from "../ui/cn";

/**
 * What this company has bought and what it made us: all time, this year, this
 * month.
 *
 * **Admins only.** Gross profit is margin, and so far only admins see margin.
 * The page does not render this for anybody else, and the route refuses them
 * as well -- the second is the one that matters, because a component nobody
 * mounts is not a permission.
 *
 * Summed from the `order` rows on the timeline, the same ones the Orders panel
 * below lists and the AM dashboard ranks by, so the three cannot disagree. "This
 * year" and "this month" are calendar periods to date, as on the dashboard.
 *
 * An order with no gross profit -- one somebody typed in -- still counts as
 * revenue, and the footer says how many there are. Treating it as zero margin
 * would understate the margin with nothing on screen to explain why.
 */
export function PerformancePanel({ companyId }: { companyId: number }) {
  const performance = useQuery({
    queryKey: queryKeys.crmPerformance(companyId),
    queryFn: () => crmApi.performance(companyId),
    staleTime: 60_000,
  });

  const data = performance.data;
  const rows: [string, PerformanceFigures][] = data
    ? [
        ["This month", data.monthToDate],
        ["This year", data.yearToDate],
        ["All time", data.allTime],
      ]
    : [];

  const missing = data?.allTime.withoutGrossProfit ?? 0;

  return (
    <section className="rounded-lg border border-line">
      <header className="flex items-baseline gap-2 border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          Performance
        </h2>
        <span
          title="Gross profit is shown to admins only"
          className="ml-auto text-[10px] uppercase tracking-wide text-faint"
        >
          Admin
        </span>
      </header>

      {performance.isLoading ? (
        <p className="px-3 py-3 text-[11px] text-faint">Loading…</p>
      ) : performance.isError || !data ? (
        <p className="px-3 py-3 text-[11px] text-faint">
          Could not load revenue for this company.
        </p>
      ) : data.allTime.orders === 0 ? (
        <p className="px-3 py-3 text-[11px] text-faint">No orders recorded yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[11px] text-faint">
              <th className="px-3 py-1.5 text-left font-normal" />
              <th className="px-3 py-1.5 text-right font-normal">Revenue</th>
              <th className="px-3 py-1.5 text-right font-normal">GP</th>
              <th className="px-3 py-1.5 text-right font-normal">Margin</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map(([label, figures]) => (
              <tr key={label}>
                <td className="px-3 py-1.5 text-faint">
                  {label}
                  <div className="text-[10px]">
                    {figures.orders} order{figures.orders === 1 ? "" : "s"}
                  </div>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-content">
                  {pounds(figures.net)}
                </td>
                <td
                  className={cn(
                    "px-3 py-1.5 text-right tabular-nums",
                    figures.grossProfit < 0 ? "text-red-600 dark:text-red-400" : "text-content",
                  )}
                >
                  {pounds(figures.grossProfit)}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-faint">
                  {margin(figures)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="border-t border-line px-3 py-1.5 text-[10px] text-faint">
        Net of VAT, from the orders recorded here
        {data?.firstOrderAt ? `, since ${when(data.firstOrderAt)}` : ""}.
        {missing > 0
          ? ` GP leaves out ${missing} order${missing === 1 ? "" : "s"} with no figure.`
          : ""}
      </footer>
    </section>
  );
}

/** Whole pounds: this is a summary, and pence across a year are noise. */
function pounds(value: number): string {
  const rounded = Math.round(value);
  const text = `£${Math.abs(rounded).toLocaleString()}`;
  return rounded < 0 ? `−${text}` : text;
}

/**
 * GP over the revenue it was earned on.
 *
 * Divided by `netWithGrossProfit`, not `net`: an order with no figure is
 * unknown margin, and counting its revenue would read it as an order that made
 * nothing. A dash when there is nothing to divide by.
 */
function margin(figures: PerformanceFigures): string {
  if (figures.netWithGrossProfit <= 0) return "—";
  return `${((figures.grossProfit / figures.netWithGrossProfit) * 100).toFixed(1)}%`;
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}
